import type { ReactNode } from 'react'
import type { DailySeries, LiveSpec } from '@/lib/market/types'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useQuote } from '@/components/market/live-provider'
import { ema, lastValue, macd, rsi, sma, stochastic } from '@/lib/market/indicators'
import { formatNumber, formatPercent, formatPrice } from '@/lib/market/format'

interface TechnicalsProps {
    series: DailySeries
    live: LiveSpec
    pivots: { pivot: number, r1: number, r2: number, r3: number, s1: number, s2: number, s3: number }
}

const Verdict = ({ bullish }: { bullish: boolean | null }): ReactNode => {
    if (bullish === null) return <span className='rounded-md bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground'>Neutral</span>

    return (
        <span className={cn('rounded-md px-1.5 py-0.5 text-[11px] font-medium', bullish ? 'bg-up/12 text-up' : 'bg-down/12 text-down')}>
            {bullish ? 'Bullish' : 'Bearish'}
        </span>
    )
}

const Technicals = ({ series, live, pivots }: TechnicalsProps): ReactNode => {
    const quote = useQuote(live)

    const analysis = useMemo(() => {
        const closes = series.c.slice()
        const highs = series.h.slice()
        const lows = series.l.slice()
        const last = closes.length - 1

        closes[last] = quote.last
        highs[last] = quote.high
        lows[last] = quote.low

        const averages = [
            { label: 'SMA 20', value: lastValue(sma(closes, 20)) },
            { label: 'SMA 50', value: lastValue(sma(closes, 50)) },
            { label: 'SMA 100', value: lastValue(sma(closes, 100)) },
            { label: 'SMA 200', value: lastValue(sma(closes, 200)) },
            { label: 'EMA 12', value: lastValue(ema(closes, 12)) },
            { label: 'EMA 26', value: lastValue(ema(closes, 26)) }
        ]

        const macdLines = macd(closes)
        const macdValue = lastValue(macdLines.macd)
        const signalValue = lastValue(macdLines.signal)
        const rsiValue = lastValue(rsi(closes, 14))
        const stoch = stochastic(highs, lows, closes)
        const stochK = lastValue(stoch.k)
        const stochD = lastValue(stoch.d)

        const oscillators = [
            {
                label: 'RSI 14',
                value: rsiValue !== null ? formatNumber(rsiValue, 1) : '-',
                bullish: rsiValue === null ? null : rsiValue <= 30 ? true : rsiValue >= 70 ? false : null,
                note: rsiValue === null ? '' : rsiValue >= 70 ? 'Overbought' : rsiValue <= 30 ? 'Oversold' : 'Within range'
            },
            {
                label: 'MACD 12, 26, 9',
                value: macdValue !== null ? formatNumber(macdValue, 3) : '-',
                bullish: macdValue === null || signalValue === null ? null : macdValue > signalValue,
                note: macdValue === null || signalValue === null ? '' : macdValue > signalValue ? 'Above signal line' : 'Below signal line'
            },
            {
                label: 'Stochastic 14, 3',
                value: stochK !== null ? formatNumber(stochK, 1) : '-',
                bullish: stochK === null || stochD === null ? null : stochK <= 20 ? true : stochK >= 80 ? false : stochK > stochD,
                note: stochK === null ? '' : stochK >= 80 ? 'Overbought' : stochK <= 20 ? 'Oversold' : stochD !== null && stochK > stochD ? 'Rising' : 'Falling'
            }
        ]

        const signals = [
            ...averages.map(item => (item.value === null ? null : quote.last > item.value)),
            ...oscillators.map(item => item.bullish)
        ]

        const bullish = signals.filter(value => value === true).length
        const bearish = signals.filter(value => value === false).length

        return { averages, oscillators, bullish, bearish, neutral: signals.length - bullish - bearish }
    }, [series, quote.last, quote.high, quote.low])

    const verdict = analysis.bullish - analysis.bearish
    const pivotRows = [
        { label: 'R3', value: pivots.r3 },
        { label: 'R2', value: pivots.r2 },
        { label: 'R1', value: pivots.r1 },
        { label: 'Pivot', value: pivots.pivot },
        { label: 'S1', value: pivots.s1 },
        { label: 'S2', value: pivots.s2 },
        { label: 'S3', value: pivots.s3 }
    ]

    return (
        <div className='space-y-5'>
            <div className='rounded-lg border border-border bg-background/50 p-3'>
                <div className='flex items-center justify-between gap-3'>
                    <p className='text-[13px] text-muted-foreground'>Signal summary</p>
                    <Verdict bullish={verdict > 1 ? true : verdict < -1 ? false : null} />
                </div>
                <div className='mt-2.5 flex h-2 gap-0.5 overflow-hidden rounded-full' aria-hidden='true'>
                    <span className='bg-up' style={{ width: `${(analysis.bullish / 9) * 100}%` }} />
                    <span className='bg-flat' style={{ width: `${(analysis.neutral / 9) * 100}%` }} />
                    <span className='bg-down' style={{ width: `${(analysis.bearish / 9) * 100}%` }} />
                </div>
                <p className='mt-2 text-xs text-muted-foreground'>
                    {analysis.bullish} bullish, {analysis.neutral} neutral and {analysis.bearish} bearish readings from moving averages and oscillators.
                </p>
            </div>

            <div>
                <h3 className='mb-1.5 text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground'>Moving averages</h3>
                <table className='w-full text-[13px]'>
                    <caption className='sr-only'>Moving averages and the price position</caption>
                    <tbody>
                        {analysis.averages.map(item => (
                            <tr key={item.label} className='border-b border-border/60 last:border-0'>
                                <th scope='row' className='py-1.5 text-left font-normal text-muted-foreground'>{item.label}</th>
                                <td className='py-1.5 text-right tabular-nums text-foreground'>{item.value !== null ? formatPrice(item.value) : '-'}</td>
                                <td className='py-1.5 pl-3 text-right tabular-nums text-muted-foreground'>{item.value !== null ? formatPercent((quote.last / item.value - 1) * 100, 1) : '-'}</td>
                                <td className='py-1.5 pl-3 text-right'><Verdict bullish={item.value === null ? null : quote.last > item.value} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div>
                <h3 className='mb-1.5 text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground'>Oscillators</h3>
                <table className='w-full text-[13px]'>
                    <caption className='sr-only'>Oscillator readings</caption>
                    <tbody>
                        {analysis.oscillators.map(item => (
                            <tr key={item.label} className='border-b border-border/60 last:border-0'>
                                <th scope='row' className='py-1.5 text-left font-normal text-muted-foreground'>{item.label}</th>
                                <td className='py-1.5 text-right tabular-nums text-foreground'>{item.value}</td>
                                <td className='py-1.5 pl-3 text-right text-xs text-muted-foreground'>{item.note}</td>
                                <td className='py-1.5 pl-3 text-right'><Verdict bullish={item.bullish} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div>
                <h3 className='mb-1.5 text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground'>Classic pivot points</h3>
                <p className='mb-2 text-xs text-muted-foreground'>From the previous session: pivot = (high + low + close) / 3.</p>
                <div className='grid grid-cols-7 gap-1 text-center text-xs'>
                    {pivotRows.map(row => (
                        <div key={row.label} className={cn('rounded-md border px-1 py-1.5', row.label === 'Pivot' ? 'border-primary/40 bg-primary/10' : 'border-border bg-background/50')}>
                            <p className='text-[10px] text-muted-foreground'>{row.label}</p>
                            <p className='mt-0.5 font-medium tabular-nums text-foreground'>{formatPrice(row.value)}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Technicals
